import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Box, Typography, IconButton, Select, MenuItem, FormControl, InputLabel,
    Checkbox, FormControlLabel, Divider, CircularProgress, Chip, Card, CardActionArea
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faIdCard, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import baseURL from '../../../user/config';

const ORG_COLORS = {
    primary: '#2D4A3D',
    text: '#1a1a2e',
    textSecondary: '#8a8f9d',
    border: '#eaecf0',
    backgroundLight: '#F8F9FB',
};

const BILLING_CYCLES = [
    { value: 'one-time', label: 'One-time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
];

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'textarea', label: 'Paragraph' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkboxes' },
];

// Format a raw digits string with thousands commas, keeping cursor friendly
const formatUGX = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '');
    if (!digits) return '';
    return Number(digits).toLocaleString('en-UG');
};
const parseUGX = (formatted) => formatted.replace(/,/g, '');

// Dynamic labels based on form_type
const LABELS = {
    membership: {
        formTitle: 'Membership Form',
        categoriesSection: 'Membership Tiers',
        categoryLabel: 'Tier',
        addCategory: 'Add Tier',
        dialogTitle: 'Create Membership Form',
        editTitle: 'Edit Membership Form',
        successCreate: 'Membership form created!',
        successEdit: 'Membership form updated!',
        typeLabel: 'Membership',
        typeDesc: 'Collect recurring or one-time membership fees with tiered pricing.',
        billingLabel: 'Billing Cycle',
        icon: faIdCard,
    },
    event: {
        formTitle: 'Event Form',
        categoriesSection: 'Ticket Types',
        categoryLabel: 'Ticket',
        addCategory: 'Add Ticket Type',
        dialogTitle: 'Create Event Form',
        editTitle: 'Edit Event Form',
        successCreate: 'Event form created!',
        successEdit: 'Event form updated!',
        typeLabel: 'Event',
        typeDesc: 'Sell tickets for an event with multiple ticket categories.',
        billingLabel: 'Ticket Type',
        icon: faCalendarAlt,
    },
};

const emptyCategory = () => ({ name: '', price: '', billing_cycle: 'one-time', description: '' });
const emptyField = () => ({ field_label: '', field_type: 'text', is_required: false, options: '' });

const TypePickerStep = ({ onSelect }) => (
    <DialogContent sx={{ pt: 3, pb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: ORG_COLORS.text, mb: 0.5, textAlign: 'center' }}>
            What are you creating?
        </Typography>
        <Typography variant="body2" sx={{ color: ORG_COLORS.textSecondary, mb: 3, textAlign: 'center' }}>
            Choose the type of form you want to set up.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {['membership', 'event'].map(type => {
                const L = LABELS[type];
                return (
                    <Card
                        key={type}
                        onClick={() => onSelect(type)}
                        sx={{
                            width: 200, border: `2px solid ${ORG_COLORS.border}`,
                            borderRadius: '12px', cursor: 'pointer',
                            transition: 'border-color 0.15s, box-shadow 0.15s',
                            '&:hover': { borderColor: ORG_COLORS.primary, boxShadow: `0 0 0 3px ${ORG_COLORS.primary}22` },
                        }}
                    >
                        <CardActionArea sx={{ p: 3, textAlign: 'center' }}>
                            <Box sx={{ fontSize: '2rem', color: ORG_COLORS.primary, mb: 1.5 }}>
                                <FontAwesomeIcon icon={L.icon} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ORG_COLORS.text, mb: 0.5 }}>
                                {L.typeLabel}
                            </Typography>
                            <Typography variant="caption" sx={{ color: ORG_COLORS.textSecondary, lineHeight: 1.4 }}>
                                {L.typeDesc}
                            </Typography>
                        </CardActionArea>
                    </Card>
                );
            })}
        </Box>
    </DialogContent>
);

const CreateFormDialog = ({ open, onClose, editData, onSuccess }) => {
    const isEdit = Boolean(editData);

    // Step: 'pick-type' | 'form'
    const [step, setStep] = useState(isEdit ? 'form' : 'pick-type');
    const [formType, setFormType] = useState('membership');

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [slug, setSlug] = useState('');
    const [status, setStatus] = useState('active');
    const [categories, setCategories] = useState([emptyCategory()]);
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);

    // Reset when dialog opens/closes
    useEffect(() => {
        if (!open) {
            setStep('pick-type');
            setFormType('membership');
            setTitle(''); setDescription(''); setSlug(''); setStatus('active');
            setCategories([emptyCategory()]); setFields([]);
            return;
        }
        if (isEdit && editData) {
            const t = editData.form_type || 'membership';
            setFormType(t);
            setStep('form');
            setTitle(editData.title || '');
            setDescription(editData.description || '');
            setSlug(editData.slug || '');
            setStatus(editData.status || 'active');
            setCategories(
                editData.categories?.length
                    ? editData.categories.map(c => ({
                        ...c,
                        // Category price is stored as UGX (BIGINT) in DB — display with commas
                        price: c.price ? formatUGX(String(Math.round(parseFloat(c.price)))) : '',
                        options: undefined,
                    }))
                    : [emptyCategory()]
            );
            setFields(
                editData.fields?.length
                    ? editData.fields.map(f => ({
                        ...f,
                        is_required: Boolean(f.is_required),
                        options: Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''),
                    }))
                    : []
            );
        }
    }, [open, isEdit, editData]);

    // Auto-generate slug from title (only in create mode)
    useEffect(() => {
        if (!isEdit && title) {
            setSlug(title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
        }
    }, [title, isEdit]);

    const L = LABELS[formType] || LABELS.membership;

    const handleTypeSelect = (type) => {
        setFormType(type);
        setStep('form');
    };

    // Categories handlers
    const addCategory = () => setCategories(prev => [...prev, emptyCategory()]);
    const removeCategory = (i) => setCategories(prev => prev.filter((_, idx) => idx !== i));
    const updateCategory = (i, key, value) =>
        setCategories(prev => prev.map((c, idx) => idx === i ? { ...c, [key]: value } : c));

    // Fields handlers
    const addField = () => setFields(prev => [...prev, emptyField()]);
    const removeField = (i) => setFields(prev => prev.filter((_, idx) => idx !== i));
    const updateField = (i, key, value) =>
        setFields(prev => prev.map((f, idx) => idx === i ? { ...f, [key]: value } : f));

    const validate = () => {
        if (!title.trim()) { toast.error('Form title is required.'); return false; }
        if (!slug.trim() || !/^[a-z0-9-]+$/.test(slug)) {
            toast.error('Slug must contain only lowercase letters, numbers, and hyphens.');
            return false;
        }
        if (categories.length === 0) { toast.error(`At least one ${L.categoryLabel.toLowerCase()} is required.`); return false; }
        for (let i = 0; i < categories.length; i++) {
            const c = categories[i];
            if (!c.name.trim()) { toast.error(`${L.categoryLabel} ${i + 1}: name is required.`); return false; }
            const rawPrice = parseUGX(c.price);
            if (!rawPrice || isNaN(parseFloat(rawPrice)) || parseFloat(rawPrice) <= 0) {
                toast.error(`${L.categoryLabel} ${i + 1}: a valid price in UGX is required.`); return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);

        try {
            let formId;

            if (isEdit) {
                const fRes = await fetch(`${baseURL}/org/membership-forms/${editData.id}`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title, description, slug, status, form_type: formType }),
                });
                const fData = await fRes.json();
                if (!fRes.ok) throw new Error(fData.error || 'Failed to update form.');
                formId = editData.id;

                // Delete existing categories and fields then re-add
                for (const cat of (editData.categories || [])) {
                    await fetch(`${baseURL}/org/membership-forms/${formId}/categories/${cat.id}`, {
                        method: 'DELETE', credentials: 'include',
                    });
                }
                for (const field of (editData.fields || [])) {
                    await fetch(`${baseURL}/org/membership-forms/${formId}/fields/${field.id}`, {
                        method: 'DELETE', credentials: 'include',
                    });
                }
            } else {
                const fRes = await fetch(`${baseURL}/org/membership-forms`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title, description, slug, form_type: formType }),
                });
                const fData = await fRes.json();
                if (!fRes.ok) throw new Error(fData.error || 'Failed to create form.');
                formId = fData.formId;
            }

            // Add categories — price stored as UGX integer
            for (let i = 0; i < categories.length; i++) {
                const cat = categories[i];
                const cRes = await fetch(`${baseURL}/org/membership-forms/${formId}/categories`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: cat.name,
                        description: cat.description || null,
                        price: Math.round(parseFloat(parseUGX(cat.price))),
                        billing_cycle: cat.billing_cycle,
                        sort_order: i,
                    }),
                });
                const cData = await cRes.json();
                if (!cRes.ok) throw new Error(cData.error || `Failed to save ${L.categoryLabel.toLowerCase()} "${cat.name}".`);
            }

            // Add fields
            for (let i = 0; i < fields.length; i++) {
                const f = fields[i];
                if (!f.field_label.trim()) continue;
                const fieldKey = f.field_label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') + `_${i}`;
                const options = ['select', 'checkbox'].includes(f.field_type) && f.options
                    ? f.options.split(',').map(o => o.trim()).filter(Boolean)
                    : null;
                const ffRes = await fetch(`${baseURL}/org/membership-forms/${formId}/fields`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        field_label: f.field_label,
                        field_type: f.field_type,
                        field_key: fieldKey,
                        is_required: f.is_required ? 1 : 0,
                        options,
                        sort_order: i,
                    }),
                });
                const ffData = await ffRes.json();
                if (!ffRes.ok) throw new Error(ffData.error || `Failed to save field "${f.field_label}".`);
            }

            toast.success(isEdit ? L.successEdit : L.successCreate);
            onSuccess();
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Type picker screen
    if (step === 'pick-type') {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: ORG_COLORS.text, pb: 0 }}>
                    New Form
                </DialogTitle>
                <TypePickerStep onSelect={handleTypeSelect} />
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} sx={{ textTransform: 'none', color: ORG_COLORS.textSecondary }}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', color: ORG_COLORS.text, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isEdit && (
                        <Button
                            size="small"
                            onClick={() => setStep('pick-type')}
                            sx={{ textTransform: 'none', color: ORG_COLORS.textSecondary, fontSize: '0.78rem', minWidth: 0, p: '2px 8px' }}
                        >
                            ← Back
                        </Button>
                    )}
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: ORG_COLORS.text }}>
                        {isEdit ? L.editTitle : L.dialogTitle}
                    </Typography>
                    <Chip
                        label={L.typeLabel}
                        size="small"
                        sx={{ ml: 1, bgcolor: `${ORG_COLORS.primary}18`, color: ORG_COLORS.primary, fontWeight: 600, fontSize: '0.72rem' }}
                    />
                    {isEdit && (
                        <FormControl size="small" sx={{ ml: 'auto', minWidth: 110 }}>
                            <Select value={status} onChange={e => setStatus(e.target.value)} sx={{ fontSize: '0.8rem' }}>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                {/* Section 1 — Form Info */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORG_COLORS.primary, mb: 1.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Form Details
                </Typography>
                <TextField
                    fullWidth label="Form Title" value={title}
                    onChange={e => setTitle(e.target.value)}
                    margin="dense" size="small" required
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    fullWidth label="Description (optional)" value={description}
                    onChange={e => setDescription(e.target.value)}
                    margin="dense" size="small" multiline rows={2}
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    fullWidth label="URL Slug" value={slug}
                    onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    margin="dense" size="small" required
                    helperText={`Public link: ${window.location.origin}/membership/${slug || 'your-slug'}`}
                    sx={{ mb: 0.5 }}
                />

                <Divider sx={{ my: 2.5 }} />

                {/* Section 2 — Categories / Tickets */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORG_COLORS.primary, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {L.categoriesSection}
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={addCategory}
                        sx={{ textTransform: 'none', color: ORG_COLORS.primary, fontSize: '0.8rem', fontWeight: 600 }}
                    >
                        {L.addCategory}
                    </Button>
                </Box>

                {categories.map((cat, i) => (
                    <Box
                        key={i}
                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, p: 1.5,
                              bgcolor: ORG_COLORS.backgroundLight, borderRadius: '8px', border: `1px solid ${ORG_COLORS.border}` }}
                    >
                        <TextField
                            label="Name" value={cat.name}
                            onChange={e => updateCategory(i, 'name', e.target.value)}
                            size="small" sx={{ flex: 2 }} required
                        />
                        <TextField
                            label="Price (UGX)" value={cat.price}
                            onChange={e => updateCategory(i, 'price', formatUGX(e.target.value))}
                            size="small" sx={{ flex: 1 }} required
                            inputProps={{ inputMode: 'numeric' }}
                        />
                        <FormControl size="small" sx={{ flex: 1.5 }}>
                            <InputLabel>{L.billingLabel}</InputLabel>
                            <Select value={cat.billing_cycle} label={L.billingLabel}
                                onChange={e => updateCategory(i, 'billing_cycle', e.target.value)}>
                                {BILLING_CYCLES.map(b => (
                                    <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Description" value={cat.description}
                            onChange={e => updateCategory(i, 'description', e.target.value)}
                            size="small" sx={{ flex: 2 }}
                        />
                        <IconButton onClick={() => removeCategory(i)} disabled={categories.length === 1} sx={{ mt: 0.5 }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '13px', color: categories.length === 1 ? '#ccc' : '#ef5350' }} />
                        </IconButton>
                    </Box>
                ))}

                <Divider sx={{ my: 2.5 }} />

                {/* Section 3 — Custom Fields */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORG_COLORS.primary, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Form Fields
                        </Typography>
                        <Typography variant="caption" sx={{ color: ORG_COLORS.textSecondary }}>
                            Name and email are always included. Add extra fields below.
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={addField}
                        sx={{ textTransform: 'none', color: ORG_COLORS.primary, fontSize: '0.8rem', fontWeight: 600 }}
                    >
                        Add Field
                    </Button>
                </Box>

                {/* Built-in fields — read-only */}
                {[
                    { label: 'Full Name', type: 'Text' },
                    { label: 'Email Address', type: 'Email' },
                ].map((f) => (
                    <Box
                        key={f.label}
                        sx={{
                            display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, p: 1.5,
                            bgcolor: '#f3f4f6', borderRadius: '8px',
                            border: `1px solid ${ORG_COLORS.border}`, opacity: 0.75,
                        }}
                    >
                        <TextField label="Field Label" value={f.label} size="small" sx={{ flex: 2.5 }} disabled />
                        <TextField label="Type" value={f.type} size="small" sx={{ flex: 1.5 }} disabled />
                        <Chip label="Required" size="small" sx={{ bgcolor: `${ORG_COLORS.primary}18`, color: ORG_COLORS.primary, fontWeight: 600, fontSize: '0.72rem' }} />
                        <Typography variant="caption" sx={{ color: ORG_COLORS.textSecondary, ml: 0.5, whiteSpace: 'nowrap' }}>
                            Built-in
                        </Typography>
                    </Box>
                ))}

                {fields.length === 0 && (
                    <Box sx={{ py: 1.5, textAlign: 'center', color: ORG_COLORS.textSecondary, fontSize: '0.85rem' }}>
                        No additional fields. Click "Add Field" to collect more information.
                    </Box>
                )}

                {fields.map((field, i) => (
                    <Box
                        key={i}
                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, p: 1.5,
                              bgcolor: ORG_COLORS.backgroundLight, borderRadius: '8px', border: `1px solid ${ORG_COLORS.border}` }}
                    >
                        <TextField
                            label="Field Label" value={field.field_label}
                            onChange={e => updateField(i, 'field_label', e.target.value)}
                            size="small" sx={{ flex: 2.5 }}
                        />
                        <FormControl size="small" sx={{ flex: 1.5 }}>
                            <InputLabel>Type</InputLabel>
                            <Select value={field.field_type} label="Type"
                                onChange={e => updateField(i, 'field_type', e.target.value)}>
                                {FIELD_TYPES.map(t => (
                                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {['select', 'checkbox'].includes(field.field_type) && (
                            <TextField
                                label="Options (comma-separated)"
                                value={field.options}
                                onChange={e => updateField(i, 'options', e.target.value)}
                                size="small" sx={{ flex: 2.5 }}
                                placeholder="Option A, Option B, Option C"
                            />
                        )}
                        <FormControlLabel
                            control={<Checkbox checked={field.is_required} size="small"
                                onChange={e => updateField(i, 'is_required', e.target.checked)} />}
                            label={<Typography sx={{ fontSize: '0.8rem' }}>Required</Typography>}
                            sx={{ mx: 0, mt: 0.5 }}
                        />
                        <IconButton onClick={() => removeField(i)} sx={{ mt: 0.5 }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '13px', color: '#ef5350' }} />
                        </IconButton>
                    </Box>
                ))}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: ORG_COLORS.textSecondary }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                        backgroundColor: ORG_COLORS.primary,
                        '&:hover': { backgroundColor: '#1e3329' },
                        minWidth: 120,
                    }}
                >
                    {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : (isEdit ? 'Save Changes' : 'Create Form')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateFormDialog;
